import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  Box, Container, Typography, Card, CardContent, Grid, Button, AppBar, Toolbar,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Chip, CardMedia, Fab, Menu, MenuItem, Avatar, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider,
  FormControl, InputLabel, Select, Autocomplete
} from '@mui/material';
import {
  Add, Edit, Delete, Visibility, BarChart, Campaign, Image, AccountCircle,
  Logout, ToggleOn, ToggleOff, CloudUpload, CreditCard, LocalOffer,
  Web, Insights
} from '@mui/icons-material';
import PageBuilderManagement from '../components/admin/PageBuilderManagement';
import AnalyticsPage from './admin/analytics/AnalyticsPage';
import { motion } from 'framer-motion';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { INDIAN_CITIES } from '../utils/indianCities';
import { GRADIENTS, RADIUS } from '../theme/tokens';

const MotionCard = motion(Card);

const DRAWER_WIDTH = 240;

function MarketingDashboard() {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Sidebar navigation
  const [activeSection, setActiveSection] = useState('banners');
  const [currentTab, setCurrentTab] = useState(0);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);

  // Banner location filter
  const [bannerLocationFilter, setBannerLocationFilter] = useState('all');

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link_url: '',
    display_order: 0,
    start_date: '',
    end_date: '',
    location: 'home',
    target_cities: []
  });

  // City filter for banner list
  const [bannerCityFilter, setBannerCityFilter] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [overviewAnalytics, setOverviewAnalytics] = useState(null);

  // Banner analytics date-range
  const [bannerAnalyticsFrom, setBannerAnalyticsFrom] = useState('');
  const [bannerAnalyticsTo, setBannerAnalyticsTo] = useState('');

  // Card-ad analytics date-range
  const [cardAdAnalyticsFrom, setCardAdAnalyticsFrom] = useState('');
  const [cardAdAnalyticsTo, setCardAdAnalyticsTo] = useState('');

  // User menu
  const [anchorEl, setAnchorEl] = useState(null);

  // Card Ads state
  const [cardAds, setCardAds] = useState([]);
  const [cardAdLoading, setCardAdLoading] = useState(false);
  const [cardAdDialogOpen, setCardAdDialogOpen] = useState(false);
  const [editCardAdDialogOpen, setEditCardAdDialogOpen] = useState(false);
  const [deleteCardAdDialogOpen, setDeleteCardAdDialogOpen] = useState(false);
  const [selectedCardAd, setSelectedCardAd] = useState(null);
  const [cardAdFormData, setCardAdFormData] = useState({
    title: '',
    offer_text: '',
    link_url: '',
    bg_color: '#F26522',
    text_color: 'white',
    offer_color: '#F7B32B',
    display_order: 0,
    start_date: '',
    end_date: '',
    target_cities: []
  });

  // Card Ad Analytics state
  const [cardAdAnalytics, setCardAdAnalytics] = useState(null);
  const [cardAdAnalyticsDialogOpen, setCardAdAnalyticsDialogOpen] = useState(false);
  const [selectedCardAdForAnalytics, setSelectedCardAdForAnalytics] = useState(null);
  const [cardAdOverviewAnalytics, setCardAdOverviewAnalytics] = useState(null);
  const [cardAdTab, setCardAdTab] = useState(0);

  useEffect(() => {
    fetchBanners();
    fetchOverviewAnalytics();
    fetchCardAds();
    fetchCardAdOverviewAnalytics();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/marketing/banners');
      setBanners(response.data.data.banners || []);
    } catch (err) {
      setError('Failed to load banners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverviewAnalytics = async () => {
    try {
      const response = await api.get('/marketing/analytics/overview');
      setOverviewAnalytics(response.data.data.banners || []);
    } catch (err) {
      console.error('Failed to load overview analytics', err);
    }
  };

  const fetchBannerAnalytics = async (bannerId, from, to) => {
    try {
      const params = {};
      if (from) params.start_date = from;
      if (to) params.end_date = to;
      const response = await api.get(`/marketing/banners/${bannerId}/analytics`, { params });
      setAnalytics(response.data.data);
    } catch (err) {
      setError('Failed to load analytics');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreate = async () => {
    try {
      if (!imageFile) {
        setError('Please select an image');
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('image', imageFile);
      if (formData.title) formDataToSend.append('title', formData.title);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.link_url) formDataToSend.append('link_url', formData.link_url);
      if (formData.display_order) formDataToSend.append('display_order', formData.display_order);
      if (formData.start_date) formDataToSend.append('start_date', formData.start_date);
      if (formData.end_date) formDataToSend.append('end_date', formData.end_date);
      formDataToSend.append('location', formData.location || 'home');
      if (formData.target_cities && formData.target_cities.length > 0) {
        formDataToSend.append('target_cities', JSON.stringify(formData.target_cities));
      }

      await api.post('/marketing/banners', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess('Banner created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchBanners();
      fetchOverviewAnalytics();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create banner');
    }
  };

  const handleEdit = async () => {
    try {
      const formDataToSend = new FormData();
      if (imageFile) formDataToSend.append('image', imageFile);
      if (formData.title !== undefined) formDataToSend.append('title', formData.title);
      if (formData.description !== undefined) formDataToSend.append('description', formData.description);
      if (formData.link_url !== undefined) formDataToSend.append('link_url', formData.link_url);
      if (formData.display_order !== undefined) formDataToSend.append('display_order', formData.display_order);
      if (formData.start_date !== undefined) formDataToSend.append('start_date', formData.start_date);
      if (formData.end_date !== undefined) formDataToSend.append('end_date', formData.end_date);
      if (formData.location !== undefined) formDataToSend.append('location', formData.location);
      formDataToSend.append('is_active', selectedBanner.is_active);
      // Send target_cities (can be empty array for nationwide)
      formDataToSend.append('target_cities', JSON.stringify(formData.target_cities || []));

      await api.put(`/marketing/banners/${selectedBanner.id}`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess('Banner updated successfully');
      setEditDialogOpen(false);
      setSelectedBanner(null);
      resetForm();
      fetchBanners();
      fetchOverviewAnalytics();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update banner');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/marketing/banners/${selectedBanner.id}`);
      setSuccess('Banner deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedBanner(null);
      fetchBanners();
      fetchOverviewAnalytics();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete banner');
    }
  };

  const handleToggleStatus = async (banner) => {
    try {
      await api.put(`/marketing/banners/${banner.id}/toggle`);
      setSuccess(`Banner ${banner.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchBanners();
      fetchOverviewAnalytics();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update banner status');
    }
  };

  const openEditDialog = (banner) => {
    setSelectedBanner(banner);
    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      link_url: banner.link_url || '',
      display_order: banner.display_order || 0,
      start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
      end_date: banner.end_date ? banner.end_date.split('T')[0] : '',
      location: banner.location || 'home',
      target_cities: banner.target_cities || []
    });
    setImagePreview(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${banner.image_path}`);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (banner) => {
    setSelectedBanner(banner);
    setDeleteDialogOpen(true);
  };

  const openAnalyticsDialog = async (banner) => {
    setSelectedBanner(banner);
    setBannerAnalyticsFrom('');
    setBannerAnalyticsTo('');
    setAnalyticsDialogOpen(true);
    await fetchBannerAnalytics(banner.id, '', '');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      link_url: '',
      display_order: 0,
      start_date: '',
      end_date: '',
      location: 'home',
      target_cities: []
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const closeAllDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setAnalyticsDialogOpen(false);
    setSelectedBanner(null);
    setAnalytics(null);
    resetForm();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Card Ads Functions
  const fetchCardAds = async () => {
    try {
      setCardAdLoading(true);
      const response = await api.get('/marketing/card-ads');
      setCardAds(response.data.data.cardAds || []);
    } catch (err) {
      console.error('Failed to load card ads', err);
    } finally {
      setCardAdLoading(false);
    }
  };

  const handleCreateCardAd = async () => {
    try {
      if (!cardAdFormData.title || !cardAdFormData.offer_text) {
        setError('Title and offer text are required');
        return;
      }

      const payload = {
        ...cardAdFormData,
        start_date: cardAdFormData.start_date || null,
        end_date: cardAdFormData.end_date || null,
        target_cities: cardAdFormData.target_cities
      };

      await api.post('/marketing/card-ads', payload);
      setSuccess('Card ad created successfully');
      setCardAdDialogOpen(false);
      resetCardAdForm();
      fetchCardAds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create card ad');
    }
  };

  const handleEditCardAd = async () => {
    try {
      const payload = {
        ...cardAdFormData,
        start_date: cardAdFormData.start_date || null,
        end_date: cardAdFormData.end_date || null,
        target_cities: cardAdFormData.target_cities
      };

      await api.put(`/marketing/card-ads/${selectedCardAd.id}`, payload);
      setSuccess('Card ad updated successfully');
      setEditCardAdDialogOpen(false);
      setSelectedCardAd(null);
      resetCardAdForm();
      fetchCardAds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update card ad');
    }
  };

  const handleDeleteCardAd = async () => {
    try {
      await api.delete(`/marketing/card-ads/${selectedCardAd.id}`);
      setSuccess('Card ad deleted successfully');
      setDeleteCardAdDialogOpen(false);
      setSelectedCardAd(null);
      fetchCardAds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete card ad');
    }
  };

  const handleToggleCardAdStatus = async (cardAd) => {
    try {
      await api.put(`/marketing/card-ads/${cardAd.id}/toggle`);
      setSuccess(`Card ad ${cardAd.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchCardAds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update card ad status');
    }
  };

  const openEditCardAdDialog = (cardAd) => {
    setSelectedCardAd(cardAd);
    setCardAdFormData({
      title: cardAd.title || '',
      offer_text: cardAd.offer_text || '',
      link_url: cardAd.link_url || '',
      bg_color: cardAd.bg_color || '#F26522',
      text_color: cardAd.text_color || 'white',
      offer_color: cardAd.offer_color || '#F7B32B',
      display_order: cardAd.display_order || 0,
      start_date: cardAd.start_date ? cardAd.start_date.split('T')[0] : '',
      end_date: cardAd.end_date ? cardAd.end_date.split('T')[0] : '',
      target_cities: Array.isArray(cardAd.target_cities) ? cardAd.target_cities : []
    });
    setEditCardAdDialogOpen(true);
  };

  const openDeleteCardAdDialog = (cardAd) => {
    setSelectedCardAd(cardAd);
    setDeleteCardAdDialogOpen(true);
  };

  const resetCardAdForm = () => {
    setCardAdFormData({
      title: '',
      offer_text: '',
      link_url: '',
      bg_color: '#F26522',
      text_color: 'white',
      offer_color: '#F7B32B',
      display_order: 0,
      start_date: '',
      end_date: '',
      target_cities: []
    });
  };

  const closeCardAdDialogs = () => {
    setCardAdDialogOpen(false);
    setEditCardAdDialogOpen(false);
    setDeleteCardAdDialogOpen(false);
    setCardAdAnalyticsDialogOpen(false);
    setSelectedCardAd(null);
    setSelectedCardAdForAnalytics(null);
    setCardAdAnalytics(null);
    resetCardAdForm();
  };

  // Card Ad Analytics Functions
  const fetchCardAdOverviewAnalytics = async () => {
    try {
      const response = await api.get('/marketing/card-ads/analytics/overview');
      setCardAdOverviewAnalytics(response.data.data);
    } catch (err) {
      console.error('Failed to load card ad overview analytics', err);
    }
  };

  const fetchCardAdAnalytics = async (cardAdId, from, to) => {
    try {
      const params = {};
      if (from) params.start_date = from;
      if (to) params.end_date = to;
      const response = await api.get(`/marketing/card-ads/${cardAdId}/analytics`, { params });
      setCardAdAnalytics(response.data.data);
    } catch (err) {
      setError('Failed to load card ad analytics');
    }
  };

  const openCardAdAnalyticsDialog = async (cardAd) => {
    setSelectedCardAdForAnalytics(cardAd);
    setCardAdAnalyticsFrom('');
    setCardAdAnalyticsTo('');
    setCardAdAnalyticsDialogOpen(true);
    await fetchCardAdAnalytics(cardAd.id, '', '');
  };

  // Card Preview Component for live preview
  const CardPreview = () => {
    const previewAds = cardAds.filter(ad => ad.is_active).slice(0, 2);
    const defaultAds = [
      { id: 1, title: 'Partner Offers', offer_text: 'Save 20%', bg_color: theme.palette.primary.main, text_color: 'white', offer_color: theme.palette.accent?.main || theme.palette.warning.main },
      { id: 2, title: 'Coaching', offer_text: 'Free Demo', bg_color: 'white', text_color: theme.palette.secondary.main, offer_color: theme.palette.primary.main }
    ];
    const displayAds = previewAds.length > 0 ? previewAds : defaultAds;

    return (
      <Box
        sx={{
          width: 280,
          borderRadius: RADIUS.md,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          background: GRADIENTS.navyLight,
          px: 2,
          py: 1.5
        }}
      >
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'white', mb: 1 }}>
          Offers (Live Preview)
        </Typography>
        <Box display="flex" gap={1}>
          {displayAds.map((ad, index) => (
            <Box
              key={ad.id || index}
              sx={{
                bgcolor: ad.bg_color || theme.palette.primary.main,
                borderRadius: RADIUS.sm,
                px: 1.5,
                py: 1,
                flex: 1
              }}
            >
              <Typography sx={{ color: ad.text_color || 'white', fontSize: '0.65rem', display: 'block' }} fontWeight="600">
                {ad.title}
              </Typography>
              <Typography sx={{ color: ad.offer_color || (theme.palette.accent?.main || theme.palette.warning.main), fontSize: '0.75rem' }} fontWeight="700">
                {ad.offer_text}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.6rem', mt: 1, display: 'block' }}>
          Card Terms: Valid for listed sessions only.
        </Typography>
      </Box>
    );
  };

  // Sidebar menu items
  const menuItems = [
    { id: 'banners', label: 'Banners', icon: <Image /> },
    { id: 'card-ads', label: 'Card Ads', icon: <CreditCard /> },
    { id: 'page-builder', label: 'Page Builder', icon: <Web /> },
    { id: 'site-analytics', label: 'Site Analytics', icon: <Insights /> },
  ];

  // Marketing-relevant analytics tab definitions (matching AnalyticsPage TABS values)
  const MARKETING_ANALYTICS_TABS = [
    { value: 'overview', label: 'Overview', icon: <BarChart sx={{ fontSize: 16 }} /> },
    { value: 'acquisition', label: 'Acquisition', icon: <Insights sx={{ fontSize: 16 }} /> },
    { value: 'behavior', label: 'Behavior', icon: <Visibility sx={{ fontSize: 16 }} /> },
    { value: 'conversions', label: 'Conversions', icon: <Campaign sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      {/* App Bar */}
      <AppBar position="sticky" elevation={0} sx={(theme) => ({ ...auroraTopbarSx(theme), color: 'text.primary', borderBottom: 1, borderColor: 'divider' })}>
        <Toolbar>
          <Campaign sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Marketing Dashboard
          </Typography>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex' }}>
        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              top: 64,
              height: 'calc(100vh - 64px)',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
              MANAGEMENT
            </Typography>
            <List disablePadding>
              {menuItems.map((item) => (
                <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={activeSection === item.id}
                    onClick={() => setActiveSection(item.id)}
                    sx={{
                      borderRadius: RADIUS.md,
                      '&.Mui-selected': {
                        bgcolor: 'primary.soft',
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.soft',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'primary.main',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Banners Section */}
          {activeSection === 'banners' && (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight="700">
                  Banner Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Location Filter */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label="All"
                      onClick={() => setBannerLocationFilter('all')}
                      color={bannerLocationFilter === 'all' ? 'primary' : 'default'}
                      variant={bannerLocationFilter === 'all' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      label="Home Page"
                      onClick={() => setBannerLocationFilter('home')}
                      color={bannerLocationFilter === 'home' ? 'primary' : 'default'}
                      variant={bannerLocationFilter === 'home' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      label="Student Dashboard"
                      onClick={() => setBannerLocationFilter('dashboard')}
                      color={bannerLocationFilter === 'dashboard' ? 'primary' : 'default'}
                      variant={bannerLocationFilter === 'dashboard' ? 'filled' : 'outlined'}
                    />
                  </Box>
                  {/* City Filter */}
                  <Autocomplete
                    size="small"
                    options={INDIAN_CITIES}
                    value={bannerCityFilter || null}
                    onChange={(e, newValue) => setBannerCityFilter(newValue || '')}
                    renderInput={(params) => (
                      <TextField {...params} label="Filter by City" placeholder="All Cities" sx={{ minWidth: 180 }} />
                    )}
                    sx={{ minWidth: 180 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setCreateDialogOpen(true)}
                    size="large"
                    sx={{ borderRadius: 3 }}
                  >
                    Create Banner
                  </Button>
                </Box>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)}>
                  <Tab label="All Banners" />
                  <Tab label="Analytics Overview" />
                </Tabs>
              </Box>

        {/* Tab Content */}
        {currentTab === 0 && (
          <Grid container spacing={3}>
            {loading ? (
              <Grid item xs={12}>
                <Typography>Loading...</Typography>
              </Grid>
            ) : banners.length === 0 ? (
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3, textAlign: 'center', py: 8 }}>
                  <Image sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No banners yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first banner to start engaging students
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    Create Banner
                  </Button>
                </Card>
              </Grid>
            ) : (
              banners
                .filter(banner => bannerLocationFilter === 'all' || banner.location === bannerLocationFilter)
                .filter(banner => {
                  if (!bannerCityFilter) return true; // No city filter, show all
                  if (!banner.target_cities || banner.target_cities.length === 0) return true; // Nationwide banner
                  return banner.target_cities.includes(bannerCityFilter);
                })
                .map((banner) => (
                <Grid item xs={12} md={6} lg={4} key={banner.id}>
                  <MotionCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    sx={{ borderRadius: 3, height: '100%' }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${banner.image_path}`}
                      alt={banner.title || 'Banner'}
                      loading="lazy"
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="h6" fontWeight="600">
                          {banner.title || 'Untitled Banner'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={banner.location === 'dashboard' ? 'Dashboard' : 'Home'}
                            color={banner.location === 'dashboard' ? 'info' : 'secondary'}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={banner.is_active ? 'Active' : 'Inactive'}
                            color={banner.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {/* Target Cities Display */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                        {(!banner.target_cities || banner.target_cities.length === 0) ? (
                          <Chip label="Nationwide" size="small" color="primary" variant="outlined" />
                        ) : (
                          <>
                            {banner.target_cities.slice(0, 3).map((city) => (
                              <Chip key={city} label={city} size="small" variant="outlined" />
                            ))}
                            {banner.target_cities.length > 3 && (
                              <Chip label={`+${banner.target_cities.length - 3} more`} size="small" variant="outlined" />
                            )}
                          </>
                        )}
                      </Box>

                      {banner.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {banner.description.length > 100
                            ? `${banner.description.substring(0, 100)}...`
                            : banner.description}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => openEditDialog(banner)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<BarChart />}
                          onClick={() => openAnalyticsDialog(banner)}
                        >
                          Analytics
                        </Button>
                        <Button
                          size="small"
                          startIcon={banner.is_active ? <ToggleOff /> : <ToggleOn />}
                          onClick={() => handleToggleStatus(banner)}
                        >
                          {banner.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Delete />}
                          color="error"
                          onClick={() => openDeleteDialog(banner)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </CardContent>
                  </MotionCard>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {currentTab === 1 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Performance Overview
              </Typography>
              {overviewAnalytics && overviewAnalytics.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Banner</strong></TableCell>
                        <TableCell align="right"><strong>Views</strong></TableCell>
                        <TableCell align="right"><strong>Clicks</strong></TableCell>
                        <TableCell align="right"><strong>CTR</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overviewAnalytics.map((item) => (
                        <TableRow key={item.banner.id}>
                          <TableCell>{item.banner.title || 'Untitled'}</TableCell>
                          <TableCell align="right">{item.views}</TableCell>
                          <TableCell align="right">{item.clicks}</TableCell>
                          <TableCell align="right">{item.ctr.toFixed(2)}%</TableCell>
                          <TableCell>
                            <Chip
                              label={item.banner.is_active ? 'Active' : 'Inactive'}
                              color={item.banner.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No analytics data available yet</Typography>
              )}
            </CardContent>
          </Card>
        )}
            </>
          )}

          {/* Page Builder Section */}
          {activeSection === 'page-builder' && (
            <PageBuilderManagement apiBase="/marketing/editable-pages" />
          )}

          {/* Site Analytics Section */}
          {activeSection === 'site-analytics' && (
            <AnalyticsPage
              apiBase="/marketing/site-analytics"
              tabs={MARKETING_ANALYTICS_TABS}
            />
          )}

          {/* Card Ads Section */}
          {activeSection === 'card-ads' && (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" fontWeight="700">
                  Card Ads Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCardAdDialogOpen(true)}
                  size="large"
                  sx={{ borderRadius: 3 }}
                >
                  Create Card Ad
                </Button>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={cardAdTab} onChange={(e, val) => setCardAdTab(val)}>
                  <Tab label="All Card Ads" />
                  <Tab label="Analytics Overview" />
                </Tabs>
              </Box>

              {cardAdTab === 0 && (
              <Grid container spacing={3}>
                {/* Live Preview */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, p: 3, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Live Preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      This is how the offers section appears on student digital library cards.
                    </Typography>
                    <CardPreview />
                  </Card>
                </Grid>

                {/* Card Ads List */}
                <Grid item xs={12} md={8}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="600" gutterBottom>
                        Active Card Ads
                      </Typography>
                      {cardAdLoading ? (
                        <Typography>Loading...</Typography>
                      ) : cardAds.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <LocalOffer sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="body1" color="text.secondary" gutterBottom>
                            No card ads yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary" mb={2}>
                            Create your first card ad to display on student library cards
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setCardAdDialogOpen(true)}
                          >
                            Create Card Ad
                          </Button>
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Preview</strong></TableCell>
                                <TableCell><strong>Title</strong></TableCell>
                                <TableCell><strong>Offer Text</strong></TableCell>
                                <TableCell><strong>Order</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {cardAds.map((cardAd) => (
                                <TableRow key={cardAd.id}>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        bgcolor: cardAd.bg_color,
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 1,
                                        width: 'fit-content',
                                        minWidth: 80
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: cardAd.text_color, display: 'block' }} fontWeight="600">
                                        {cardAd.title}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: cardAd.offer_color }} fontWeight="700">
                                        {cardAd.offer_text}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{cardAd.title}</TableCell>
                                  <TableCell>{cardAd.offer_text}</TableCell>
                                  <TableCell>{cardAd.display_order}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={cardAd.is_active ? 'Active' : 'Inactive'}
                                      color={cardAd.is_active ? 'success' : 'default'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <IconButton size="small" onClick={() => openEditCardAdDialog(cardAd)}>
                                        <Edit fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => openCardAdAnalyticsDialog(cardAd)}>
                                        <BarChart fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleToggleCardAdStatus(cardAd)}>
                                        {cardAd.is_active ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                                      </IconButton>
                                      <IconButton size="small" color="error" onClick={() => openDeleteCardAdDialog(cardAd)}>
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              )}

              {/* Analytics Overview Tab */}
              {cardAdTab === 1 && (
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Card Ads Performance
                    </Typography>
                    {cardAdOverviewAnalytics ? (
                      <>
                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                              <Typography variant="h4" color="primary">{cardAdOverviewAnalytics.totals?.totalViews || 0}</Typography>
                              <Typography variant="caption">Total Views</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                              <Typography variant="h4" color="primary">{cardAdOverviewAnalytics.totals?.totalClicks || 0}</Typography>
                              <Typography variant="caption">Total Clicks</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                              <Typography variant="h4" color="success.main">
                                {(cardAdOverviewAnalytics.totals?.overallCtr || 0).toFixed(2)}%
                              </Typography>
                              <Typography variant="caption">Overall CTR</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                              <Typography variant="h4" color="primary">{cardAdOverviewAnalytics.cardAds?.length || 0}</Typography>
                              <Typography variant="caption">Total Ads</Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Performance Table */}
                        {cardAdOverviewAnalytics.cardAds && cardAdOverviewAnalytics.cardAds.length > 0 ? (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Ad Preview</strong></TableCell>
                                  <TableCell><strong>Title</strong></TableCell>
                                  <TableCell align="right"><strong>Views</strong></TableCell>
                                  <TableCell align="right"><strong>Clicks</strong></TableCell>
                                  <TableCell align="right"><strong>CTR</strong></TableCell>
                                  <TableCell><strong>Status</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {cardAdOverviewAnalytics.cardAds.map((item) => (
                                  <TableRow key={item.cardAd.id}>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          bgcolor: item.cardAd.bg_color,
                                          borderRadius: 1,
                                          px: 1,
                                          py: 0.5,
                                          width: 'fit-content',
                                          minWidth: 60
                                        }}
                                      >
                                        <Typography variant="caption" sx={{ color: item.cardAd.text_color, display: 'block', fontSize: '0.65rem' }} fontWeight="600">
                                          {item.cardAd.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: item.cardAd.offer_color, fontSize: '0.7rem' }} fontWeight="700">
                                          {item.cardAd.offer_text}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>{item.cardAd.title}</TableCell>
                                    <TableCell align="right">{item.views}</TableCell>
                                    <TableCell align="right">{item.clicks}</TableCell>
                                    <TableCell align="right">{item.ctr.toFixed(2)}%</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={item.cardAd.is_active ? 'Active' : 'Inactive'}
                                        color={item.cardAd.is_active ? 'success' : 'default'}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography color="text.secondary">No analytics data available yet</Typography>
                        )}
                      </>
                    ) : (
                      <Typography color="text.secondary">Loading analytics...</Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={closeAllDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Create New Banner</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<CloudUpload />}
                sx={{ py: 2 }}
              >
                {imageFile ? imageFile.name : 'Upload Banner Image *'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              {imagePreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title (Optional)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (Optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link URL (Optional)"
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                placeholder="https://example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Display Location *</InputLabel>
                <Select
                  value={formData.location}
                  label="Display Location *"
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                >
                  <MenuItem value="home">Home Page (Public)</MenuItem>
                  <MenuItem value="dashboard">Student Dashboard</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date (Optional)"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date (Optional)"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={INDIAN_CITIES}
                value={formData.target_cities}
                onChange={(e, newValue) => setFormData({ ...formData, target_cities: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Cities (Leave empty for Nationwide)"
                    placeholder="Select cities..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog - Similar to Create */}
      <Dialog open={editDialogOpen} onClose={closeAllDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Edit Banner</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<CloudUpload />}
                sx={{ py: 2 }}
              >
                {imageFile ? imageFile.name : 'Change Banner Image (Optional)'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              {imagePreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link URL"
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Display Location</InputLabel>
                <Select
                  value={formData.location}
                  label="Display Location"
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                >
                  <MenuItem value="home">Home Page (Public)</MenuItem>
                  <MenuItem value="dashboard">Student Dashboard</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={INDIAN_CITIES}
                value={formData.target_cities || []}
                onChange={(e, newValue) => setFormData({ ...formData, target_cities: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Cities (Leave empty for Nationwide)"
                    placeholder="Select cities..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeAllDialogs}>
        <DialogTitle>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this banner? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onClose={closeAllDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Banner Analytics - {selectedBanner?.title || 'Untitled'}</DialogTitle>
        <DialogContent>
          {/* Date range picker */}
          <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={bannerAnalyticsFrom}
                onChange={(e) => {
                  setBannerAnalyticsFrom(e.target.value);
                  if (selectedBanner) fetchBannerAnalytics(selectedBanner.id, e.target.value, bannerAnalyticsTo);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={bannerAnalyticsTo}
                onChange={(e) => {
                  setBannerAnalyticsTo(e.target.value);
                  if (selectedBanner) fetchBannerAnalytics(selectedBanner.id, bannerAnalyticsFrom, e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {analytics ? (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{analytics.summary.totalViews}</Typography>
                    <Typography variant="caption">Total Views</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{analytics.summary.totalClicks}</Typography>
                    <Typography variant="caption">Total Clicks</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{analytics.summary.uniqueViewers}</Typography>
                    <Typography variant="caption">Unique Viewers</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {analytics.summary.ctr.toFixed(2)}%
                    </Typography>
                    <Typography variant="caption">Click-Through Rate</Typography>
                  </Paper>
                </Grid>
                {analytics.summary.uniqueClickers != null && (
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">{analytics.summary.uniqueClickers}</Typography>
                      <Typography variant="caption">Unique Clickers</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* Daily breakdown chart */}
              {analytics.dailyBreakdown && analytics.dailyBreakdown.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Daily Breakdown</Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={analytics.dailyBreakdown} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <ReTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="views" stroke="#1976d2" dot={false} strokeWidth={2} name="Views" />
                      <Line type="monotone" dataKey="clicks" stroke="#2e7d32" dot={false} strokeWidth={2} name="Clicks" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {analytics.topViewers && analytics.topViewers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Top Viewers</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="right">Views</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.topViewers.slice(0, 5).map((viewer, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{viewer.user?.name}</TableCell>
                            <TableCell>{viewer.user?.email}</TableCell>
                            <TableCell align="right">{viewer.viewCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {analytics.topClickers && analytics.topClickers.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Top Clickers</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.topClickers.slice(0, 5).map((clicker, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{clicker.user?.name}</TableCell>
                            <TableCell>{clicker.user?.email}</TableCell>
                            <TableCell align="right">{clicker.clickCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>Loading analytics...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Card Ad Create Dialog */}
      <Dialog open={cardAdDialogOpen} onClose={closeCardAdDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Create Card Ad</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title *"
                value={cardAdFormData.title}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, title: e.target.value })}
                placeholder="e.g., Partner Offers"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Offer Text *"
                value={cardAdFormData.offer_text}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, offer_text: e.target.value })}
                placeholder="e.g., Save 20%"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link URL (Optional)"
                value={cardAdFormData.link_url}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, link_url: e.target.value })}
                placeholder="https://example.com"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Background Color"
                type="color"
                value={cardAdFormData.bg_color}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, bg_color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Text Color"
                type="color"
                value={cardAdFormData.text_color === 'white' ? '#ffffff' : cardAdFormData.text_color}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, text_color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Offer Color"
                type="color"
                value={cardAdFormData.offer_color}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, offer_color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={cardAdFormData.display_order}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, display_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date (Optional)"
                value={cardAdFormData.start_date}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date (Optional)"
                value={cardAdFormData.end_date}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={INDIAN_CITIES}
                value={cardAdFormData.target_cities}
                onChange={(e, newValue) => setCardAdFormData({ ...cardAdFormData, target_cities: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Cities (Leave empty for Nationwide)"
                    placeholder="Select cities..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Preview:
              </Typography>
              <Box
                sx={{
                  bgcolor: cardAdFormData.bg_color,
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  width: 'fit-content',
                  minWidth: 120
                }}
              >
                <Typography variant="caption" sx={{ color: cardAdFormData.text_color, display: 'block' }} fontWeight="600">
                  {cardAdFormData.title || 'Title'}
                </Typography>
                <Typography variant="body2" sx={{ color: cardAdFormData.offer_color }} fontWeight="700">
                  {cardAdFormData.offer_text || 'Offer Text'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCardAdDialogs}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCardAd}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Card Ad Edit Dialog */}
      <Dialog open={editCardAdDialogOpen} onClose={closeCardAdDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Card Ad</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title *"
                value={cardAdFormData.title}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Offer Text *"
                value={cardAdFormData.offer_text}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, offer_text: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link URL (Optional)"
                value={cardAdFormData.link_url}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, link_url: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Background Color"
                type="color"
                value={cardAdFormData.bg_color}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, bg_color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Text Color"
                type="color"
                value={cardAdFormData.text_color === 'white' ? '#ffffff' : cardAdFormData.text_color}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, text_color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Offer Color"
                type="color"
                value={cardAdFormData.offer_color}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, offer_color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={cardAdFormData.display_order}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, display_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date (Optional)"
                value={cardAdFormData.start_date}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date (Optional)"
                value={cardAdFormData.end_date}
                onChange={(e) => setCardAdFormData({ ...cardAdFormData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={INDIAN_CITIES}
                value={cardAdFormData.target_cities || []}
                onChange={(e, newValue) => setCardAdFormData({ ...cardAdFormData, target_cities: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Cities (Leave empty for Nationwide)"
                    placeholder="Select cities..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Preview:
              </Typography>
              <Box
                sx={{
                  bgcolor: cardAdFormData.bg_color,
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  width: 'fit-content',
                  minWidth: 120
                }}
              >
                <Typography variant="caption" sx={{ color: cardAdFormData.text_color, display: 'block' }} fontWeight="600">
                  {cardAdFormData.title || 'Title'}
                </Typography>
                <Typography variant="body2" sx={{ color: cardAdFormData.offer_color }} fontWeight="700">
                  {cardAdFormData.offer_text || 'Offer Text'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCardAdDialogs}>Cancel</Button>
          <Button variant="contained" onClick={handleEditCardAd}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Card Ad Delete Dialog */}
      <Dialog open={deleteCardAdDialogOpen} onClose={closeCardAdDialogs}>
        <DialogTitle>Delete Card Ad</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this card ad? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCardAdDialogs}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteCardAd}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Card Ad Analytics Dialog */}
      <Dialog open={cardAdAnalyticsDialogOpen} onClose={closeCardAdDialogs} maxWidth="md" fullWidth>
        <DialogTitle>
          Card Ad Analytics - {selectedCardAdForAnalytics?.title || 'Untitled'}
        </DialogTitle>
        <DialogContent>
          {/* Date range picker */}
          <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={cardAdAnalyticsFrom}
                onChange={(e) => {
                  setCardAdAnalyticsFrom(e.target.value);
                  if (selectedCardAdForAnalytics) fetchCardAdAnalytics(selectedCardAdForAnalytics.id, e.target.value, cardAdAnalyticsTo);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={cardAdAnalyticsTo}
                onChange={(e) => {
                  setCardAdAnalyticsTo(e.target.value);
                  if (selectedCardAdForAnalytics) fetchCardAdAnalytics(selectedCardAdForAnalytics.id, cardAdAnalyticsFrom, e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {cardAdAnalytics ? (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{cardAdAnalytics.summary?.totalViews || 0}</Typography>
                    <Typography variant="caption">Total Views</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{cardAdAnalytics.summary?.totalClicks || 0}</Typography>
                    <Typography variant="caption">Total Clicks</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{cardAdAnalytics.summary?.uniqueViewers || 0}</Typography>
                    <Typography variant="caption">Unique Viewers</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {(cardAdAnalytics.summary?.ctr || 0).toFixed(2)}%
                    </Typography>
                    <Typography variant="caption">Click-Through Rate</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Daily stats chart */}
              {cardAdAnalytics.dailyStats && cardAdAnalytics.dailyStats.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Daily Stats</Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={cardAdAnalytics.dailyStats} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <ReTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="views" stroke="#1976d2" dot={false} strokeWidth={2} name="Views" />
                      <Line type="monotone" dataKey="clicks" stroke="#2e7d32" dot={false} strokeWidth={2} name="Clicks" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Top Viewers */}
              {cardAdAnalytics.topViewers && cardAdAnalytics.topViewers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Top Viewers</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="right">Views</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cardAdAnalytics.topViewers.slice(0, 5).map((viewer, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{viewer.user?.name || 'Unknown'}</TableCell>
                            <TableCell>{viewer.user?.email || 'N/A'}</TableCell>
                            <TableCell align="right">{viewer.viewCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Top Clickers */}
              {cardAdAnalytics.topClickers && cardAdAnalytics.topClickers.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Top Clickers</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cardAdAnalytics.topClickers.slice(0, 5).map((clicker, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{clicker.user?.name || 'Unknown'}</TableCell>
                            <TableCell>{clicker.user?.email || 'N/A'}</TableCell>
                            <TableCell align="right">{clicker.clickCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>Loading analytics...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCardAdDialogs}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MarketingDashboard;
